/* Rank complete matching brand groups while keeping Shopify's native matching and filters. */
(() => {
  'use strict';
  if (window.FAFSearchRanking) return;

  const vendorParameter = 'filter.p.vendor';
  const typeParameter = 'filter.p.product_type';
  const cache = new Map();
  const spellingCache = new Map();
  const cacheLimit = 16;
  const cacheLifetime = 30000;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const hasPhrase = (query, phrase) => Boolean(phrase) && (' ' + query + ' ').includes(' ' + phrase + ' ');
  const genericBrandWords = new Set(['furniture', 'mattress', 'mattresses', 'home', 'homes', 'collection', 'collections', 'living', 'design', 'designs', 'by', 'the', 'and', 'co', 'company', 'inc', 'llc', 'ltd', 'usa', 'manufacturing', 'international', 'industries', 'of', 'for']);
  const ambiguousBrandWords = new Set(['new', 'classic', 'american', 'global', 'standard', 'house', 'sleep', 'best', 'modern', 'national', 'united']);
  const parameters = value => new URLSearchParams(value instanceof URLSearchParams ? value.toString() : value || '');

  function abortIfNeeded(signal) {
    if (signal?.aborted) throw new DOMException('Search request cancelled', 'AbortError');
  }

  async function boundedRequest(signal, operation) {
    abortIfNeeded(signal);
    const controller = new AbortController();
    const relay = () => controller.abort();
    signal?.addEventListener('abort', relay, { once: true });
    let timedOut = false;
    let timer;
    try {
      return await Promise.race([
        operation(controller.signal),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new Error('Search request timed out. Please try again.'));
          }, 15000);
        }),
      ]);
    } catch (error) {
      abortIfNeeded(signal);
      // A timeout is retryable, unlike a shopper cancelling/changing search.
      if (timedOut) throw new Error('Search request timed out. Please try again.');
      throw error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', relay);
    }
  }

  function brandSettings(config) {
    const vendors = [...new Set((config.vendors || []).filter(value => typeof value === 'string' && value.trim()))];
    const aliases = (config.priorityBrands || ['Ashley', 'Benchcraft', 'Signature Design']).map(normalize).filter(Boolean);
    return { vendors, aliases };
  }

  function explicitBrand(query, brands) {
    if (/\bvendor\s*:/i.test(query)) return true;
    const normalizedQuery = normalize(query);
    if (brands.aliases.some(alias => hasPhrase(normalizedQuery, alias))) return true;
    return brands.vendors.some(vendor => {
      const name = normalize(vendor);
      if (hasPhrase(normalizedQuery, name)) return true;
      const words = name.split(' ').filter(word => !genericBrandWords.has(word));
      if ((words.length > 1 || (words.length === 1 && !ambiguousBrandWords.has(words[0]))) && hasPhrase(normalizedQuery, words.join(' '))) return true;
      return words.some(word => word.length >= 4 && !ambiguousBrandWords.has(word) && hasPhrase(normalizedQuery, word));
    });
  }

  function sectionFromHTML(html, sectionId) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const section = document.getElementById('shopify-section-' + sectionId) || document.querySelector('[data-faf-search-page][data-section-id]');
    const count = section?.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount;
    if (!section || !/^\d+$/.test(count || '') || !Number.isSafeInteger(Number(count))) throw new Error('Invalid search response');
    return section;
  }

  function nativeFilterValues(section, parameter) {
    return [...new Set(Array.from(section.querySelectorAll('input[name]'))
      .filter(input => input.name === parameter && !input.disabled && input.value)
      .map(input => input.value))];
  }

  function filterValue(raw, nativeValues) {
    // Form values have already passed through the HTML parser. JSON script
    // contents have not: Shopify can supply "Stearns &amp; Foster" there.
    // Exact/opaque values must be preserved, including grouped filter GIDs.
    if (nativeValues.includes(raw) || /^gid:\/\//.test(raw)) return raw;
    const encode = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const canonical = nativeValues.find(value => encode(value) === raw);
    if (canonical !== undefined) return canonical;
    // Predictive sections do not render form inputs. Decode entities once in
    // an inert text node, never repeatedly and never as executable markup.
    const text = document.createElement('textarea');
    text.innerHTML = raw.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return text.value;
  }

  function responseFilterValues(section, parameter) {
    const native = nativeFilterValues(section, parameter);
    const selector = parameter === vendorParameter ? '[data-faf-search-brands]' : '[data-faf-search-types]';
    let data;
    try { data = JSON.parse(section.querySelector(selector)?.textContent || 'null'); } catch { /* Native inputs remain usable. */ }
    if (data?.parameter === parameter && Array.isArray(data.values)) {
      return [...new Set(data.values.filter(item => typeof item.value === 'string' && item.value && Number(item.count) > 0)
        .map(item => filterValue(item.value, native)))];
    }
    return native;
  }

  function responseBrands(section, aliases) {
    let data;
    try { data = JSON.parse(section.querySelector('[data-faf-search-brands]')?.textContent || 'null'); } catch { /* Invalid metadata falls back to native ordering. */ }
    if (!data || data.parameter !== vendorParameter || !Array.isArray(data.values) || !data.values.length) throw new Error('Brand filters unavailable');
    const values = new Map();
    const native = nativeFilterValues(section, vendorParameter);
    data.values.forEach(item => {
      if (typeof item.value !== 'string' || !item.value || typeof item.label !== 'string') throw new Error('Invalid brand filter');
      if (Number(item.count) === 0) return;
      const value = filterValue(item.value, native);
      const preferred = aliases.some(alias => hasPhrase(normalize(item.label), alias) || hasPhrase(normalize(value), alias));
      if (values.has(value) && values.get(value) !== preferred) throw new Error('Ambiguous brand filter');
      values.set(value, preferred);
    });
    return {
      preferred: Array.from(values).filter(([, preferred]) => preferred).map(([value]) => value),
      other: Array.from(values).filter(([, preferred]) => !preferred).map(([value]) => value),
    };
  }

  async function requestSection(params, sectionId, signal, searchUrl) {
    abortIfNeeded(signal);
    const url = new URL(searchUrl, window.location.origin);
    url.search = parameters(params).toString();
    // Shopify's preview bootstrap redirect can collapse repeated facet keys.
    // Once the rendered page confirms this preview theme, its preview cookie
    // already pins same-origin AJAX requests; keep all facet parameters intact.
    const preview = url.searchParams.get('preview_theme_id');
    if (preview && String(window.Shopify?.theme?.id || '') === preview) url.searchParams.delete('preview_theme_id');
    url.searchParams.set('section_id', sectionId);
    const scope = [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang].join('|');
    const key = scope + '|' + url.href;
    const saved = cache.get(key);
    if (saved && Date.now() - saved.time < cacheLifetime) {
      cache.delete(key);
      cache.set(key, saved);
      return sectionFromHTML(saved.html, sectionId);
    }
    if (saved) cache.delete(key);
    const html = await boundedRequest(signal, async requestSignal => {
      const response = await fetch(url.href, { signal: requestSignal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Search request failed');
      return response.text();
    });
    abortIfNeeded(signal);
    const section = sectionFromHTML(html, sectionId);
    cache.set(key, { html, time: Date.now() });
    while (cache.size > cacheLimit) cache.delete(cache.keys().next().value);
    return section;
  }

  function readSection(section) {
    const count = Number(section.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount);
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid search count');
    const cards = Array.from(section.querySelectorAll('li[data-faf-search-product]'));
    const list = cards[0]?.parentElement || section.querySelector('ul.predictive-search__results-list, ul.collection-results');
    if (cards.some(card => card.parentElement !== list)) throw new Error('Invalid search card list');
    return { section, count, cards, list };
  }

  function oneSpellingChange(input, candidate) {
    if (input === candidate || input.slice(0, 4) !== candidate.slice(0, 4) || Math.abs(input.length - candidate.length) > 1) return false;
    if (input.length === candidate.length) {
      const different = Array.from(input).map((letter, index) => letter === candidate[index] ? -1 : index).filter(index => index >= 0);
      return different.length === 1 || (different.length === 2 && different[1] === different[0] + 1 &&
        input[different[0]] === candidate[different[1]] && input[different[1]] === candidate[different[0]]);
    }
    const shorter = input.length < candidate.length ? input : candidate;
    const longer = input.length < candidate.length ? candidate : input;
    let index = 0;
    while (index < shorter.length && shorter[index] === longer[index]) index += 1;
    return shorter.slice(index) === longer.slice(index + 1);
  }

  async function spellingCandidate(plan, signal, searchUrl) {
    const url = new URL(searchUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '/suggest.json';
    url.search = new URLSearchParams({ q: plan.spellingTerm, 'resources[type]': 'query,product',
      'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][fields]': 'title' }).toString();
    const scope = [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang].join('|');
    const key = scope + '|' + url.href;
    const saved = spellingCache.get(key);
    if (saved && Date.now() - saved.time < cacheLifetime) return saved.candidate;
    abortIfNeeded(signal);
    const results = await boundedRequest(signal, async requestSignal => {
      const response = await fetch(url.href, { signal: requestSignal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Spelling suggestions unavailable');
      return (await response.json())?.resources?.results;
    });
    abortIfNeeded(signal);
    if (!results || !Array.isArray(results.products) || !Array.isArray(results.queries)) throw new Error('Invalid spelling suggestions');
    const input = normalize(plan.spellingTerm);
    const candidates = new Map();
    const completions = new Map();
    const terms = [...results.queries.slice(0, 6).map(item => item?.text),
      ...results.products.slice(0, 6).flatMap(item => typeof item?.title === 'string' ? item.title.match(/[\p{L}]+/gu) || [] : [])];
    terms.forEach(term => {
      if (typeof term !== 'string' || !/^[\p{L}]{5,30}$/u.test(term)) return;
      const word = normalize(term);
      if (oneSpellingChange(input, word)) candidates.set(word, term);
      if (input.length >= 5 && word.length > input.length && word.startsWith(input)) completions.set(word, term);
    });
    const uniqueCandidates = new Map([...candidates, ...completions]);
    const candidate = uniqueCandidates.size === 1 ? uniqueCandidates.values().next().value : '';
    spellingCache.set(key, { candidate, time: Date.now() });
    while (spellingCache.size > cacheLimit) spellingCache.delete(spellingCache.keys().next().value);
    return candidate;
  }

  function markCorrection(section, correction) {
    [section, section.querySelector('[data-faf-search-page]'), section.querySelector('[data-faf-search-count]')].filter(Boolean).forEach(element => {
      element.dataset.fafSearchCorrection = correction;
      element.dataset.fafQueryMatch = 'spelling';
    });
  }

  async function matchingSection(base, plan, sectionId, signal, searchUrl) {
    if (plan.literalEngineQuery && plan.literalEngineQuery !== base.get('q')) {
      const literal = parameters(base);
      literal.set('q', plan.literalEngineQuery);
      try {
        const section = await requestSection(literal, sectionId, signal, searchUrl);
        // The total, rather than cards on this page, keeps a direct page 2+
        // request in the same result set even when that page is empty.
        if (readSection(section).count > 0) {
          section.dataset.fafQueryMatch = 'literal';
          return { params: literal, section };
        }
      } catch (error) {
        abortIfNeeded(signal);
        // An unavailable exact query must not disable native search recovery.
      }
    }
    const section = await requestSection(base, sectionId, signal, searchUrl);
    section.dataset.fafQueryMatch = plan.literalEngineQuery ? 'fuzzy' : 'native';
    if (plan.spellingTerm && plan.spellingScope && readSection(section).count === 0) {
      try {
        const candidate = await spellingCandidate(plan, signal, searchUrl);
        if (candidate) {
          const corrected = parameters(base);
          corrected.set('q', plan.spellingScope + ' AND (title:"' + candidate + '")');
          const verified = await requestSection(corrected, sectionId, signal, searchUrl);
          // Suggestions provide a spelling only. Existing Shopify search must
          // independently enforce outdoor/category/price/vendor constraints.
          const matching = readSection(verified);
          if (matching.count > 0 && matching.cards.every(card => {
            try { return hasPhrase(normalize(JSON.parse(card.querySelector('[data-faf-search-product-data]').textContent).title), normalize(candidate)); }
            catch { return false; }
          })) {
            markCorrection(verified, candidate);
            return { params: corrected, section: verified };
          }
        }
      } catch (error) {
        abortIfNeeded(signal);
        // An optional spelling lookup never replaces a valid empty response
        // with a network error, nor does it trigger another spelling attempt.
      }
    }
    return { params: base, section };
  }

  function cardId(card) {
    try {
      const value = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
      return value?.id ? String(value.id) : null;
    } catch { return null; }
  }

  function validCards(cards, expected) {
    const ids = cards.map(cardId);
    return cards.length === expected && ids.every(Boolean) && new Set(ids).size === ids.length;
  }

  function belongsToTier(card, preferred, aliases) {
    try {
      const data = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
      if (typeof data?.vendor !== 'string') return false;
      return aliases.some(alias => hasPhrase(normalize(data.vendor), alias)) === preferred;
    } catch { return false; }
  }

  function markPriority(section, status, preferred, other, countCapped = false) {
    const targets = [section, section.querySelector('[data-faf-search-page]'), section.querySelector('[data-faf-search-count]')].filter(Boolean);
    targets.forEach(target => {
      target.dataset.fafBrandPriority = status;
      if (Number.isSafeInteger(preferred)) target.dataset.fafPreferredCount = String(preferred);
      if (Number.isSafeInteger(other)) target.dataset.fafOtherCount = String(other);
      if (countCapped) target.dataset.fafBrandCountCapped = 'true';
    });
    return section;
  }

  function partitionParams(base, vendors) {
    const params = parameters(base);
    params.delete(vendorParameter);
    params.delete('page');
    vendors.forEach(vendor => params.append(vendorParameter, vendor));
    return params;
  }

  async function fetchSection(humanParams, sectionId, options = {}) {
    const params = parameters(humanParams);
    const requestedPage = Number(params.get('page') || 1);
    if (!Number.isSafeInteger(requestedPage) || requestedPage < 1) throw new Error('Invalid search page');
    const stream = await createStream(params, sectionId, options);
    if (stream) {
      let section = stream.section;
      for (let page = 1; page < requestedPage; page += 1) {
        const alreadyDone = stream.done;
        section = (await stream.next({ signal: options.signal })).section;
        if (alreadyDone) break;
      }
      return section;
    }
    const api = window.FAFSearch;
    if (!api?.toEngineParams || !api?.plan) throw new Error('Search integration unavailable');
    const matching = await matchingSection(api.toEngineParams(params), api.plan(params.get('q'), params), sectionId,
      options.signal, api.config?.searchUrl || window.location.pathname);
    return markPriority(matching.section, 'native');
  }

  // Native counts are estimates, and native pagination is capped at 1,000.
  // Each cursor follows real page links. A saturated cursor is divided into
  // disjoint product-level native facets, then resumed lazily. Price-sorted
  // partitions merge their next cards; relevance retains the preferred-brand
  // tiers and Shopify's native ordering within each matching partition.
  async function createStream(humanParams, sectionId, options = {}) {
    const api = window.FAFSearch;
    if (!api?.toEngineParams || !api?.plan) return null;
    const params = parameters(humanParams);
    params.delete('page');
    const plan = api.plan(params.get('q'), params);
    const size = Number(options.pageSize) || (sectionId === 'faf-search-suggestions' ? 8 : 24);
    if (!plan.productIntent || !plan.query || !Number.isSafeInteger(size) || size < 1 || size > 50) return null;
    const config = api.config || {};
    const searchUrl = config.searchUrl || window.location.pathname;
    const searchPath = new URL(searchUrl, window.location.origin).pathname;
    let base = parameters(api.toEngineParams(params));
    base.delete('page');
    const brands = brandSettings(config);
    const matching = await matchingSection(base, plan, sectionId, options.signal, searchUrl);
    base = matching.params;
    const canonical = matching.section;
    const all = readSection(canonical);
    if (!all.list || canonical.querySelector('.template-search__item:not([data-faf-search-product])')) return null;
    const sort = params.get('sort_by') || 'relevance';
    const priceDirection = sort === 'price-ascending' ? 1 : sort === 'price-descending' ? -1 : 0;
    const useGroups = sort === 'relevance' && !params.getAll(vendorParameter).some(value => value.trim()) &&
      !explicitBrand(plan.correctedQuery || plan.query, brands) && brands.aliases.length > 0;
    let groups;
    if (useGroups && all.count) {
      try { groups = responseBrands(canonical, brands.aliases); } catch { /* Use the complete native stream if facets are unavailable. */ }
    }
    const grouped = Boolean(groups?.preferred.length && groups?.other.length);
    const cursor = (values, preferred, seed = null, depth = 0) => ({
      params: parameters(values), preferred, depth, page: 0, nextPage: 1, done: false,
      probe: false, buffer: [], seen: new Set(), seed, children: null, childIndex: 0,
      maxCount: 0, overlap: false, replayed: false, retriedPages: new Set(), titleTerms: new Set(),
    });
    let state = {
      cursors: grouped ? [cursor(partitionParams(base, groups.preferred), true), cursor(partitionParams(base, groups.other), false)] :
        [cursor(base, null, canonical)],
      tier: 0, loaded: 0, seen: new Set(), done: false, partitioned: false,
    };
    let busy = false;
    const stream = {};
    const cloneCursor = item => ({ ...item, params: parameters(item.params), buffer: item.buffer.slice(), seen: new Set(item.seen), retriedPages: new Set(item.retriedPages), titleTerms: new Set(item.titleTerms),
      children: item.children?.map(cloneCursor) || null });
    const copyState = source => ({ ...source, seen: new Set(source.seen), cursors: source.cursors.map(cloneCursor) });
    const productData = card => {
      try {
        const data = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
        if (data?.id) return data;
      } catch { /* Fail closed when matching evidence is missing. */ }
      throw new Error('Search product information unavailable');
    };
    const matches = card => {
      if (!plan.postFilter) return true;
      if (typeof api.matchesProduct !== 'function') throw new Error('Search matching unavailable');
      const result = api.matchesProduct(productData(card), plan);
      if (result === null || result === undefined) throw new Error('Search product matching information unavailable');
      return result === true;
    };
    const price = card => {
      const value = productData(card).price;
      if (!Number.isFinite(Number(value)) || value === null || value === '') throw new Error('Search price unavailable');
      return Number(value);
    };

    function readNativePage(section, item) {
      const part = readSection(section);
      if (section.querySelector('.template-search__item:not([data-faf-search-product])') || part.cards.length > size ||
        !validCards(part.cards, part.cards.length) || (item.preferred !== null &&
          part.cards.some(card => !belongsToTier(card, item.preferred, brands.aliases)))) throw new Error('Invalid progressive search page');
      const pageElement = section.querySelector('.pagination-wrapper[data-page]');
      const nextLink = section.querySelector('a.pagination__item--next[href]');
      if (pageElement && Number(pageElement.dataset.page) !== item.nextPage &&
        !(item.probe && !part.cards.length && !nextLink)) throw new Error('Unexpected native search page');
      let nextPage = null;
      if (nextLink) {
        const url = new URL(nextLink.getAttribute('href'), new URL(searchUrl, window.location.origin));
        const value = url.searchParams.get('page') || '';
        const sameValues = key => JSON.stringify(url.searchParams.getAll(key).sort()) === JSON.stringify(item.params.getAll(key).sort());
        const scopedKeys = [...new Set([...url.searchParams.keys(), ...item.params.keys()])]
          .filter(key => key.startsWith('filter.') || key === 'sort_by' || key === 'type' || key.startsWith('options['));
        if (url.origin !== window.location.origin || url.pathname !== searchPath || !/^\d+$/.test(value) ||
          !Number.isSafeInteger(Number(value)) || Number(value) !== item.nextPage + 1 || !part.cards.length ||
          !sameValues('q') || scopedKeys.some(key => !sameValues(key))) throw new Error('Invalid native search continuation');
        nextPage = Number(value);
      }
      // Never adopt a next link wholesale: Shopify may omit preview/locale
      // parameters there. Future requests always clone the original scope.
      const probe = !nextLink && part.cards.length === size && item.nextPage * size < 1000;
      if (probe) nextPage = item.nextPage + 1;
      return { ...part, nextPage, probe };
    }

    function divide(item, section, draft) {
      // Vendor values also cover products without a product type. Preserve
      // any explicit filter by splitting only its selected, canonical values.
      for (const parameter of [vendorParameter, typeParameter]) {
        let values = responseFilterValues(section, parameter);
        const selected = item.params.getAll(parameter);
        if (selected.length) values = values.filter(value => selected.includes(value));
        if (values.length < 2) continue;
        if (item.depth >= 16) throw new Error('Search partition limit reached');
        const middle = Math.ceil(values.length / 2);
        item.children = [values.slice(0, middle), values.slice(middle)].map(group => {
          const scoped = parameters(item.params);
          scoped.delete(parameter);
          scoped.delete('page');
          group.forEach(value => scoped.append(parameter, value));
          const child = cursor(scoped, item.preferred, null, item.depth + 1);
          child.titleTerms = new Set(item.titleTerms);
          child.replayed = item.replayed;
          return child;
        });
        item.childIndex = 0;
        item.nextPage = null;
        item.seed = null;
        draft.partitioned = true;
        return true;
      }
      // A single vendor/type can itself contain more than 1,000 products
      // (for example Meridian stationary sectionals). Divide its existing
      // query into a title-family match and its Boolean complement. These
      // branches cover the whole parent query without overlapping or relying
      // on variant facets, which could otherwise assign one product twice.
      if (item.depth >= 16) return false;
      const titles = readSection(section).cards.map(card => String(productData(card).title || ''));
      const common = new Set(['the', 'and', 'with', 'for', 'set', 'sets', 'piece', 'pieces', 'product', 'products', 'furniture',
        'modular', 'sectional', 'sectionals', 'stationary', 'reclining', 'sofa', 'sofas', 'bed', 'beds', 'mattress', 'mattresses']);
      const words = titles.map(title => title.match(/[\p{L}\p{N}]+/gu) || []);
      const usable = term => /\p{L}/u.test(term) && term.length >= 3 && !common.has(normalize(term)) && !item.titleTerms.has(normalize(term));
      let terms = [...new Set(words.map(parts => parts[0]).filter(Boolean))].filter(usable);
      if (!terms.length) terms = [...new Set(words.flat())].filter(usable);
      if (!terms.length) return false;
      terms = terms.slice(0, Math.min(8, Math.max(1, Math.ceil(terms.length / 2))));
      const clause = terms.map(term => 'title:"' + term.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"').join(' OR ');
      item.children = [false, true].map(exclude => {
        const scoped = parameters(item.params);
        scoped.delete('page');
        scoped.set('q', '(' + (scoped.get('q') || '*') + ') AND ' + (exclude ? 'NOT ' : '') + '(' + clause + ')');
        const child = cursor(scoped, item.preferred, null, item.depth + 1);
        child.titleTerms = new Set([...item.titleTerms, ...terms.map(normalize)]);
        child.replayed = item.replayed;
        return child;
      });
      item.childIndex = 0;
      item.nextPage = null;
      item.seed = null;
      draft.partitioned = true;
      return true;
    }

    function responseSection(cards, draft) {
      const section = canonical.cloneNode(true);
      readSection(section).list.replaceChildren(...cards.map((card, index) => {
        const copy = card.cloneNode(true);
        if (sectionId === 'faf-search-suggestions') copy.id = 'faf-search-option-' + (index + 1);
        return copy;
      }));
      const totalEstimate = draft.done ? draft.loaded : Math.max(all.count, draft.loaded + 1);
      const wrapper = section.matches('[data-faf-search-page]') ? section : section.querySelector('[data-faf-search-page]');
      const targets = [section, wrapper, ...section.querySelectorAll('[data-faf-search-count]')].filter(Boolean);
      targets.forEach(element => {
        element.dataset.fafCountEstimated = String(!draft.done);
        element.dataset.fafMatchedCount = String(draft.loaded);
        element.dataset.fafStreamLoaded = String(draft.loaded);
        element.dataset.fafStreamDone = String(draft.done);
        if (draft.done && !draft.loaded) element.dataset.fafPostFilterEmpty = 'true';
      });
      section.querySelectorAll('[data-faf-search-count]').forEach(element => {
        element.dataset.fafSearchCount = String(draft.loaded);
      });
      if (wrapper && draft.done && !draft.loaded) wrapper.dataset.fafPostFilterEmpty = 'true';
      const pagination = section.querySelector('.pagination-wrapper');
      if (pagination) {
        const logicalPage = Math.max(1, Math.ceil(draft.loaded / size));
        pagination.dataset.page = String(logicalPage);
        const current = pagination.querySelector('[aria-current="page"]');
        if (current) current.textContent = String(logicalPage);
        const next = pagination.querySelector('a.pagination__item--next[href]');
        if (draft.done) next?.closest('li')?.remove();
        else if (next) {
          const url = new URL(searchUrl, window.location.origin);
          url.search = params.toString();
          url.searchParams.set('page', String(logicalPage + 1));
          next.setAttribute('href', url.pathname + url.search);
        }
      }
      markPriority(section, grouped ? 'applied' : 'native', undefined, undefined, draft.partitioned);
      return { section, done: draft.done, loaded: draft.loaded, totalEstimate };
    }

    async function next(nextOptions = {}) {
      if (busy) throw new Error('Progressive search is already loading');
      const signal = nextOptions.signal || options.signal;
      abortIfNeeded(signal);
      if (state.done) return responseSection([], state);
      busy = true;
      const draft = copyState(state);
      const cards = [];
      let requests = 0;

      const ensure = async item => {
        while (!item.buffer.length && !item.done) {
          abortIfNeeded(signal);
          if (item.children) {
            let chosen = null;
            if (priceDirection) {
              // Fetch only the head of each branch. Recursive binary splits
              // keep small uncapped partitions lazy and globally price sorted.
              for (const child of item.children) await ensure(child);
              for (const child of item.children) {
                if (!child.buffer.length) continue;
                if (!chosen || (price(child.buffer[0]) - price(chosen.buffer[0])) * priceDirection < 0) chosen = child;
              }
            } else {
              while (item.childIndex < item.children.length) {
                const child = item.children[item.childIndex];
                await ensure(child);
                if (child.buffer.length) { chosen = child; break; }
                item.childIndex += 1;
              }
            }
            if (!chosen) { item.done = true; continue; }
            const card = chosen.buffer.shift();
            const id = cardId(card);
            if (!item.seen.has(id)) { item.seen.add(id); item.buffer.push(card); }
            continue;
          }

          if (++requests > 128) throw new Error('Search needs more specific filters to finish matching');
          const query = parameters(item.params);
          if (item.nextPage > 1) query.set('page', String(item.nextPage));
          const native = item.seed || await requestSection(query, sectionId, signal, searchUrl);
          abortIfNeeded(signal);
          item.seed = null;
          const part = readNativePage(native, item);
          item.maxCount = Math.max(item.maxCount, part.count);
          if (item.page === 0 && part.count >= 1000) {
            if (sort !== 'relevance' && !priceDirection) throw new Error('This search sort needs a more specific filter');
            if (!divide(item, native, draft)) throw new Error('Search has more results than Shopify can page; choose a category or brand');
            continue;
          }
          const fresh = part.cards.filter(card => !item.seen.has(cardId(card)));
          if (fresh.length !== part.cards.length && !item.replayed && !item.retriedPages.has(item.nextPage)) {
            // Recheck a moving page boundary before exposing the next cards.
            // In particular, a price-sorted page must not reveal later prices
            // before a temporarily skipped earlier product is recovered.
            item.retriedPages.add(item.nextPage);
            cache.clear();
            continue;
          }
          if (fresh.length !== part.cards.length) item.overlap = true;
          if (part.cards.length && !fresh.length && !item.replayed) {
            item.replayed = true;
            if (divide(item, native, draft)) continue;
            throw new Error('Repeated page in progressive search');
          }
          fresh.forEach(card => {
            item.seen.add(cardId(card));
            if (matches(card)) item.buffer.push(card);
          });
          item.page = item.nextPage;
          item.nextPage = part.nextPage;
          item.probe = part.probe;
          item.done = part.nextPage === null;
          if (item.done && !item.replayed && (item.overlap || item.seen.size < item.maxCount)) {
            // An overlapping/changing native boundary can skip a product.
            // Reconcile once via smaller native partitions (or a bounded
            // replay for a single facet leaf), deduplicating observed IDs.
            // Children inherit this flag: uncertain native totals must not
            // recursively replay the same catalog at every partition level.
            item.replayed = true;
            item.done = false;
            cache.clear();
            if (!divide(item, native, draft)) { item.nextPage = 1; item.probe = false; }
          }
        }
      };
      try {
        while (cards.length < size && draft.tier < draft.cursors.length) {
          const item = draft.cursors[draft.tier];
          await ensure(item);
          if (!item.buffer.length) { draft.tier += 1; continue; }
          const card = item.buffer.shift();
          const id = cardId(card);
          if (draft.seen.has(id)) continue;
          draft.seen.add(id);
          cards.push(card);
        }
        // Endpoint lookahead makes a full final batch exact, rather than
        // promising one more empty page. Buffered cards survive transactionally.
        while (draft.tier < draft.cursors.length) {
          const item = draft.cursors[draft.tier];
          await ensure(item);
          if (item.buffer.length) break;
          draft.tier += 1;
        }
        draft.loaded += cards.length;
        draft.done = draft.tier >= draft.cursors.length;
        if (!draft.done && cards.length !== size) throw new Error('Incomplete progressive search batch');
        const response = responseSection(cards, draft);
        abortIfNeeded(signal);
        // A request failure or cancellation never advances native cursors or
        // discards buffered cards. Retrying starts at the same committed state.
        state = draft;
        Object.assign(stream, response);
        return response;
      } catch (error) {
        cache.clear();
        throw error;
      } finally { busy = false; }
    }
    stream.next = next;
    await next({ signal: options.signal });
    return stream;
  }

  window.FAFSearchRanking = { fetchSection, createStream };
})();
